import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, forkJoin, map, Observable, of, Subject } from 'rxjs';

import { App, Project, ProjectFieldGroup, SelectableField } from '../../models';
import { SNACKBAR_CONSTANTS } from '../../shared/constants';
import { FieldsDataService } from '../data-services/fields-data.service';
import { SelectableFieldsServiceModel } from './models';

interface ProjectFieldsResult {
    fields: SelectableField[];
    hasError: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SelectableFieldsLogicService {

    private _selectableFieldsReady = new Subject<boolean>();

    selectableFieldsReady$ = this._selectableFieldsReady.asObservable();

    model: SelectableFieldsServiceModel = new SelectableFieldsServiceModel();

    constructor(
        private _fieldsDataService: FieldsDataService,
        private _snackbar: MatSnackBar,
    ) { }

    initModel(): void {
        this.model = new SelectableFieldsServiceModel();
    }

    getSelectableFieldsAndGridFields(app: App, project: Project): SelectableField[] {
        this.model.selectableFieldsLoading = true;
        const selectableFieldsRequest$ = project.isSummaryProject === true
            ? this._fieldsDataService.getSummaryProjectSelectableFieldsAndGridFields(app, project)
            : this._fieldsDataService.getSelectableFieldsAndGridFields(app, project);

        selectableFieldsRequest$.subscribe({
            next: (selectableFields: SelectableField[]) => {
                this.model.selectableFields = selectableFields;
                this.model.associatedProjectId = project.id ?? null;
                this.model.selectableFieldsLoading = false;
                this._selectableFieldsReady.next(true);
            },
            error: () => {
                this.model.selectableFields = [];
                this.model.associatedProjectId = null;
                this.model.selectableFieldsLoading = false;
                this._selectableFieldsReady.next(false);
            }
        });
        return this.model.selectableFields;
    }

    getSelectableFieldsForProjects(app: App, projects: Project[]): Observable<ProjectFieldsResult[]> {
        return forkJoin(projects.map((project: Project) => {
            const selectableFieldsRequest$ = project.isSummaryProject === true
                ? this._fieldsDataService.getSummaryProjectSelectableFields(app, project)
                : this._fieldsDataService.getSelectableFields(app, project);

            return selectableFieldsRequest$.pipe(
                map((fields: SelectableField[]) => ({ fields, hasError: false })),
                catchError(() => of({ fields: [] as SelectableField[], hasError: true }))
            );
        }));
    }

    loadProjectFieldGroups(app: App, projects: Project[]): Observable<ProjectFieldGroup[]> {
        this.model.projectFieldGroupsLoading = true;

        return this.getSelectableFieldsForProjects(app, projects).pipe(
            map((resultsByProject: ProjectFieldsResult[]) => {
                return projects.map((project: Project, index: number) => {
                    const group = new ProjectFieldGroup();
                    group.project = project;
                    group.fields = resultsByProject[index].fields;
                    group.hasLoadError = resultsByProject[index].hasError;
                    group.isExpanded = index === 0;
                    return group;
                });
            }),
            catchError(() => of([] as ProjectFieldGroup[])),
            map((projectFieldGroups: ProjectFieldGroup[]) => {
                this.model.projectFieldGroups = projectFieldGroups;
                this.model.projectFieldGroupsLoading = false;

                const failedProjectNames = projectFieldGroups
                    .filter((group: ProjectFieldGroup) => group.hasLoadError)
                    .map((group: ProjectFieldGroup) => group.project?.name ?? app.aliases.project.singular);
                if (failedProjectNames.length > 0) {
                    this._snackbar.open(
                        SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR(failedProjectNames.join(', '), 'loading fields for'),
                        SNACKBAR_CONSTANTS.CLOSE,
                        { duration: 3000 }
                    );
                }

                projectFieldGroups.forEach((group: ProjectFieldGroup) => {
                    if (group.project?.id) {
                        this.model.projectNamesById.set(group.project.id, group.project.name ?? '');
                    }
                    group.fields.forEach((selectableField: SelectableField) => {
                        this.model.selectableFieldsByHash.set(selectableField.hash, selectableField);
                        this.model.projectNamesByHash.set(selectableField.hash, group.project?.name ?? '');
                        if (group.project?.id) {
                            this.model.projectIdsByHash.set(selectableField.hash, group.project?.id);
                            if (selectableField.identifier?.fieldId) {
                                this.model.selectableFieldsByProjectAndId.set(
                                    `${group.project.id}:${selectableField.identifier.fieldId}`,
                                    selectableField
                                );
                            }
                        }
                    });
                });
                return projectFieldGroups;
            })
        );
    }

    getSelectableFieldByHash(hash: string): SelectableField | undefined {
        return this.model.selectableFieldsByHash.get(hash);
    }

    getProjectNameByHash(hash: string): string | undefined {
        return this.model.projectNamesByHash.get(hash);
    }

    getSelectableField(projectId: string | undefined, fieldId: string | undefined): SelectableField | undefined {
        if (!projectId || !fieldId) {
            return undefined;
        }

        return this.model.selectableFieldsByProjectAndId.get(`${projectId}:${fieldId}`);
    }

    getProjectNameById(projectId: string | undefined): string | undefined {
        if (!projectId) {
            return undefined;
        }

        return this.model.projectNamesById.get(projectId);
    }

    registerFieldOrigin(fieldId: string, projectId: string | undefined, selectableField: SelectableField): void {
        this.model.fieldOriginsById.set(fieldId, { projectId, selectableField });
    }

    restoreFieldToProjectGroup(projectId: string | undefined, fieldId: string, dataId?: string): void {
        const origin = this.model.fieldOriginsById.get(fieldId);
        const selectableField = origin?.selectableField ??
            (dataId ? this.getSelectableFieldByHash(dataId) : undefined) ??
            this.getSelectableField(projectId, fieldId);
        if (!selectableField) {
            return;
        }

        const ownerProjectId = origin?.projectId ?? this.model.projectIdsByHash.get(selectableField.hash) ?? projectId;
        const projectFieldGroup = this.model.projectFieldGroups.find((group: ProjectFieldGroup) => {
            return group.project?.id === ownerProjectId;
        });
        if (!projectFieldGroup || projectFieldGroup.fields.some((field: SelectableField) => field.hash === selectableField.hash)) {
            return;
        }

        projectFieldGroup.fields = [...projectFieldGroup.fields, selectableField];
    }
}
